import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import { IFormSection } from "../../types/common";
import {
  API_ACTIVE_PARTNERS,
  API_PAYMENT_ITEM_LIST_ACTIVE,
} from "../../utils/apis";
import {
  createColumn,
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_PARTNER_ITEM_MAPPING } from "../../utils/urls";
import useGet from "../common/useGet";

const url = URL_PARTNER_ITEM_MAPPING;

export const usePartnerItemMapping = () => {
  const tableColumns = () => [
    createColumn("partner_name", true, "Partner Name"),
    createColumn("item_name", true, "Item Name"),
    createColumn("is_offline", true, "Is Offline"),
    createColumn("is_active", true, "Is Active"),
    createColumn("created_at", true, "Created At"),
    createColumn("actions", false, "Actions"),
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        is_offline: <BooleanTags status={item?.is_offline} />,
        is_active: <BooleanTags status={item?.is_active} />,
        created_at: formatDateTime(item?.created_at),
        actions: <FormActions actions={[...createViewEdit(url, item?.id)]} />,
      };
    }),
  ];

  const viewData = (item) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Partner Item Mapping Details",
          fields: {
            partner_name: item?.partner_name,
            item_name: item?.item_name,
            is_offline: <BooleanTags status={item?.is_offline} />,
            is_active: <BooleanTags status={item?.is_active} />,
            updated_at: formatDateTime(item?.updated_at),
            created_at: formatDateTime(item?.created_at),
            created_by: item?.created_by,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const usePartnerItemMappingForm = () => {
  const {
    loadData: getPartners,
    data: partners,
    loading: partnersLoading,
    error: partnersError,
  } = useGet();

  const {
    loadData: getItemNames,
    data: itemNames,
    loading: itemNamesLoading,
    error: itemNamesError,
  } = useGet();

  useEffect(() => {
    getPartners({ api: API_ACTIVE_PARTNERS });
    getItemNames({ api: API_PAYMENT_ITEM_LIST_ACTIVE });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Item Partner Details",
      fields: [
        {
          field_name: "partner",
          label: "Partner Name",
          type: "select",
          props: {
            required: true,
          },
          options_data: partners?.partners?.map((item: any) => ({
            id: item.id,
            name: item.partner_name,
          })),
        },
        {
          field_name: "item",
          label: "Item Name",
          type: "select",
          props: {
            required: true,
          },
          options_data: itemNames?.items?.map((item: any) => ({
            id: item.id,
            name: item.item_name,
          })),
        },
        {
          field_name: "callback_url",
          label: "Callback URL",
          type: "text",
          props: {
            required: false,
          },
        },
        {
          field_name: "is_active",
          label: "Is Active",
          type: "select",
          props: {
            required: false,
          },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
        {
          field_name: "is_offline",
          label: "Is Offline",
          type: "select",
          props: {
            required: false,
          },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  return {
    sections,
    redirect,
    partnersLoading,
    partnersError,
    itemNamesLoading,
    itemNamesError,
    defaultValues: {
      is_active: true,
    },
  };
};
